/**
 * System Health — per-integration status dashboard.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { RefreshCw, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_STYLE = (p) => {
  if (p.ok === false) return { fg: "#ef4444", bg: "rgba(239,68,68,0.10)", border: "#ef444444", label: "DOWN", Icon: XCircle };
  if (p.ok === true)  return { fg: "#10b981", bg: "rgba(16,185,129,0.10)", border: "#10b98144", label: "OK",   Icon: CheckCircle2 };
  return                { fg: "#64748b", bg: "rgba(100,116,139,0.10)", border: "#64748b44", label: "PENDING", Icon: AlertTriangle };
};

export default function SystemHealthPage() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [probing, setProbing] = useState(false);

  const load = async () => {
    const [h, a] = await Promise.all([
      axios.get(`${API}/system/health`, { withCredentials: true }),
      axios.get(`${API}/system/alerts`, { withCredentials: true }),
    ]);
    setData(h.data); setAlerts(a.data.alerts || []);
  };

  useEffect(() => { load().catch(() => {}); }, []);

  const probeNow = async () => {
    setProbing(true);
    try {
      await axios.post(`${API}/system/health/probe`, {}, { withCredentials: true });
      await load();
      toast.success("Health probe complete");
    } catch (e) {
      toast.error("Probe failed");
    } finally { setProbing(false); }
  };

  if (!data) return <div style={{ color: "#94a3b8" }}>Loading…</div>;

  return (
    <div data-testid="system-health-page" style={{ maxWidth: 1100, margin: "0 auto" }}>
      <div className="flex items-center justify-between" style={{ marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 28, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>
            System Health · Integrations
          </h1>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 6 }}>
            Live ping for every external provider. Alerts flow to email + webhooks on state change.
          </p>
        </div>
        <button onClick={probeNow} disabled={probing} data-testid="probe-now-btn"
          style={{
            background: "#2563eb", color: "#fff", padding: "8px 18px",
            borderRadius: 8, border: "none", cursor: "pointer",
            fontSize: 12, fontWeight: 700,
            display: "inline-flex", alignItems: "center", gap: 6,
          }}>
          {probing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />} Probe Now
        </button>
      </div>

      {/* Provider grid */}
      <div className="grid grid-cols-2 gap-3" style={{ marginBottom: 22 }}>
        {(data.providers || []).map((p) => {
          const s = STATUS_STYLE(p);
          const Icon = s.Icon;
          return (
            <div key={p.slug} data-testid={`health-card-${p.slug}`}
                 style={{
                   background: "#0d1117", border: `1px solid ${s.border}`,
                   borderRadius: 12, padding: 18,
                 }}>
              <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                <div style={{ color: "#f1f5f9", fontWeight: 700, fontSize: 14 }}>{p.label}</div>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "2px 8px", borderRadius: 999, background: s.bg,
                  border: `1px solid ${s.border}`, color: s.fg,
                  fontSize: 10, fontWeight: 800, letterSpacing: 0.6,
                }}>
                  <Icon size={10} /> {s.label}
                </span>
              </div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>
                Category: {p.category} · Mode: {p.mode} · {(p.modules || []).length} module(s)
              </div>
              <div style={{ fontSize: 10, color: "#475569", marginTop: 6 }}>
                Latency: {p.latency_ms != null ? `${p.latency_ms} ms` : "—"} ·
                Last check: {p.last_checked_at ? new Date(p.last_checked_at).toLocaleString() : "—"}
              </div>
              {p.error && <div style={{ marginTop: 6, fontSize: 11, color: "#ef4444" }}>{p.error}</div>}
            </div>
          );
        })}
      </div>

      {/* Recent alerts */}
      <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, padding: 18 }}>
        <h3 style={{ color: "#f1f5f9", fontSize: 14, fontWeight: 700, margin: 0, marginBottom: 12 }}>
          Recent Alerts
        </h3>
        {alerts.length === 0 && <div style={{ color: "#64748b", fontSize: 12 }}>No alerts in window.</div>}
        {alerts.map((a, i) => (
          <div key={i} data-testid={`alert-row-${i}`} style={{
            padding: "10px 0", borderTop: i > 0 ? "1px solid #1e2530" : "none",
            display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12,
          }}>
            <div>
              <span style={{
                padding: "2px 8px", borderRadius: 999,
                background: a.status === "DOWN" ? "rgba(239,68,68,0.12)" : "rgba(16,185,129,0.12)",
                color: a.status === "DOWN" ? "#ef4444" : "#10b981",
                fontWeight: 700, fontSize: 10, marginRight: 8,
              }}>{a.status}</span>
              <strong style={{ color: "#f1f5f9" }}>{a.slug}</strong>
              {a.error && <span style={{ color: "#94a3b8", marginLeft: 8 }}>{a.error}</span>}
            </div>
            <div style={{ color: "#64748b", fontSize: 10 }}>
              {a.created_at ? new Date(a.created_at).toLocaleString() : ""}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
